import type { TrainingHomeLevel } from "@/lib/api/training-simulator/training-simulator";
import { useEnterTrainingSession } from "@/lib/hooks/training-simulator/use-enter-training-session.ts";
import { cn } from "@/lib/utils.ts";
import {
  ArrowPathIcon,
  CheckIcon,
  LockClosedIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { Moodies } from "@somethings-ui/core/assets";
import { Button } from "@somethings-ui/web";
import { memo } from "react";

interface TrainingLevelRowProps {
  level: TrainingHomeLevel;
  accentClassName: string;
}

const STATUS_LABEL: Record<TrainingHomeLevel["state"], string> = {
  completed: "Completed",
  start: "Up next",
  resume: "Up next",
  locked: "Upcoming",
};

function TrainingLevelRowComponent({
  level,
  accentClassName,
}: TrainingLevelRowProps) {
  const { enterSession, isStarting } = useEnterTrainingSession();
  const isActive = level.state === "start" || level.state === "resume";
  const isResume = level.state === "resume";
  const isCompleted = level.state === "completed" || level.isRetry;

  const renderAction = () => {
    if (isActive) {
      return (
        <Button
          variant={level.isRetry ? "secondary" : "primary"}
          leadingIcon={<PlayIcon className="size-4" />}
          fullWidth={false}
          loading={isStarting}
          disabled={isStarting}
          onClick={() => enterSession(level.id, isResume ? "resume" : "start")}
        >
          {isResume ? "Resume" : "Start"}
        </Button>
      );
    }

    if (isCompleted) {
      return (
        <Button
          variant="secondary"
          leadingIcon={<ArrowPathIcon className="size-4" />}
          fullWidth={false}
          loading={isStarting}
          disabled={isStarting}
          onClick={() => enterSession(level.id, "retry")}
        >
          Retry
        </Button>
      );
    }

    return null;
  };

  const action = renderAction();

  return (
    <div className="flex w-full items-center justify-between border-b border-surfaces-divider bg-surfaces-z3 p-6">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-12 shrink-0 items-center justify-center rounded-full",
            isActive && accentClassName,
            isCompleted && "bg-brand-trueBlack",
            level.state === "locked" &&
              "border-[1.5px] border-dashed border-brand-trueBlack opacity-50",
          )}
        >
          {isCompleted ? (
            <CheckIcon
              className="size-5 text-brand-trueWhite"
              strokeWidth={3}
            />
          ) : isActive ? (
            <img
              src={Moodies.blackSmileyMini}
              alt=""
              aria-hidden
              className="size-8"
            />
          ) : (
            <LockClosedIcon className="size-5 text-brand-trueBlack" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-body-3 text-text-secondary">
            Level {level.position}
          </p>
          <p className="text-header-5 text-text-primary">{level.title}</p>
          <p className="text-body-3 text-text-secondary">
            {isCompleted
              ? STATUS_LABEL["completed"]
              : STATUS_LABEL[level.state]}
          </p>
        </div>
      </div>

      {action}
    </div>
  );
}

export const TrainingLevelRow = memo(TrainingLevelRowComponent);
